/**
 * Confirmação na tela do MÉDICO de que a recepção foi avisada pra mandar o
 * próximo entrar (07/10/2026) — pelo botão "Mandar próximo entrar" OU pelo
 * "Atlas, manda entrar / chama o próximo". Escuta o evento que o
 * [[receptionAlert]] dispara a cada envio e acompanha o aviso no RTDB:
 *   1. "Recepção avisada" — o aviso chegou ao servidor (pop-up na recepção);
 *   2. "Secretária deu ciente" — o aviso sumiu (ela apertou "Ciente");
 *   3. sem ciente em 2 min → "Ainda sem ciente da recepção" e some.
 * Montado no Shell, ao lado do [[CallNextWatcher]] (o lado da recepção).
 */
import { useEffect, useState } from 'react';
import { Check, CheckCheck, Clock, X } from 'lucide-react';
import {
  CALL_NEXT_SENT_EVENT,
  ReceptionAlertService,
  type CallNextSentDetail,
} from '@/services/firebase/receptionAlert';

type Stage = 'enviado' | 'ciente' | 'semCiente';

/** Quanto esperar o "Ciente" antes de desistir de acompanhar (ms). */
const WAIT_ACK_MS = 2 * 60 * 1000;
/** Quanto o cartão fica depois do "Ciente" / da desistência (ms). */
const LINGER_MS = 4000;

export const CallNextConfirm = () => {
  const [sent, setSent] = useState<CallNextSentDetail | null>(null);
  const [stage, setStage] = useState<Stage>('enviado');

  useEffect(() => {
    const onSent = (e: Event) => {
      const detail = (e as CustomEvent<CallNextSentDetail>).detail;
      if (!detail?.alertId) return;
      setStage('enviado');
      setSent(detail);
    };
    window.addEventListener(CALL_NEXT_SENT_EVENT, onSent);
    return () => window.removeEventListener(CALL_NEXT_SENT_EVENT, onSent);
  }, []);

  // Acompanha o aviso até o "Ciente" (ou até cansar de esperar).
  useEffect(() => {
    if (!sent) return;
    let seen = false;
    const unsub = ReceptionAlertService.watch(sent.clinicId, sent.alertId, (exists) => {
      if (exists) seen = true;
      else if (seen) setStage('ciente');
    });
    const giveUp = window.setTimeout(
      () => setStage((s) => (s === 'enviado' ? 'semCiente' : s)),
      WAIT_ACK_MS,
    );
    return () => {
      unsub();
      window.clearTimeout(giveUp);
    };
  }, [sent]);

  // Depois do desfecho, o cartão some sozinho.
  useEffect(() => {
    if (!sent || stage === 'enviado') return;
    const t = window.setTimeout(() => setSent(null), LINGER_MS);
    return () => window.clearTimeout(t);
  }, [sent, stage]);

  if (!sent) return null;

  const view = {
    enviado: {
      Icon: Check,
      tone: 'bg-success text-on-primary',
      title: 'Recepção avisada',
      sub: 'Aguardando a secretária dar ciente…',
    },
    ciente: {
      Icon: CheckCheck,
      tone: 'bg-success text-on-primary',
      title: 'Secretária deu ciente',
      sub: 'O próximo paciente já vai entrar.',
    },
    semCiente: {
      Icon: Clock,
      tone: 'bg-amber text-on-primary',
      title: 'Ainda sem ciente da recepção',
      sub: 'O aviso foi enviado, mas ninguém confirmou.',
    },
  }[stage];

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[210] w-[min(92vw,22rem)] animate-fade-in"
    >
      <div className="flex items-center gap-3 rounded-2xl bg-surface border border-outline-variant shadow-float px-4 py-3">
        <span className={`shrink-0 grid place-items-center w-9 h-9 rounded-full ${view.tone}`}>
          <view.Icon size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-on-surface leading-tight">{view.title}</p>
          <p className="text-xs text-on-surface-variant">{view.sub}</p>
        </div>
        <button
          onClick={() => setSent(null)}
          className="shrink-0 p-1 rounded-full text-on-surface-variant hover:bg-surface-container"
          aria-label="Fechar"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};
